import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User as SupabaseUser } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { AuthContextType, AuthState, LoginFormData, RegisterFormData, User } from '../types/auth'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    isLoggingOut: false
  })

  // 转换 Supabase 用户为我们的用户类型
  const transformUser = (supabaseUser: SupabaseUser | null): User | null => {
    if (!supabaseUser) return null
    
    return {
      id: supabaseUser.id,
      email: supabaseUser.email || '',
      username: supabaseUser.user_metadata?.username,
      avatar_url: supabaseUser.user_metadata?.avatar_url,
      created_at: supabaseUser.created_at,
      updated_at: supabaseUser.updated_at || supabaseUser.created_at
    }
  }

  // 初始化认证状态
  useEffect(() => {
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          setState({
            user: transformUser(session.user),
            loading: false,
            error: null,
            isLoggingOut: false
          })
        } else {
          setState({
            user: null,
            loading: false,
            error: null,
            isLoggingOut: false
          })
        }
      } catch (error) {
        // 静默处理未认证错误，这是正常情况
        console.debug('No existing session found, user needs to login')
        setState({
          user: null,
          loading: false,
          error: null,
          isLoggingOut: false
        })
      }
    }

    getInitialSession()

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_, session) => {
        setState({
          user: transformUser(session?.user || null),
          loading: false,
          error: null,
          isLoggingOut: false
        })
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // 登录
  const login = async (data: LoginFormData) => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      })

      if (error) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }))
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: '登录失败，请稍后重试'
      }))
    }
  }

  // 注册
  const register = async (data: RegisterFormData) => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      const { error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            username: data.username
          }
        }
      })

      if (error) {
        console.debug('Registration error:', error.message)
        setState(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }))
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          error: null,
          isLoggingOut: false
        }))
      }
    } catch (error) {
      console.debug('Registration exception:', error)
      setState(prev => ({
        ...prev,
        loading: false,
        error: '注册失败，请稍后重试'
      }))
    }
  }

  // 登出
  const logout = async () => {
    // 防止重复点击
    if (state.loading) {
      return
    }

    setState(prev => ({ ...prev, loading: true, error: null, isLoggingOut: true }))
    
    try {
      // 方法1: 尝试正常的服务器登出
      let { error } = await supabase.auth.signOut()
      
      if (error) {
        // 方法2: 如果服务器登出失败，尝试本地登出
        await supabase.auth.signOut({
          scope: 'local'
        })
      }
      
      // 无论哪种方式，都清除本地状态
      setState({
        user: null,
        loading: false,
        error: null,
        isLoggingOut: false
      })
      
      // 清除本地存储中的会话数据
      try {
        localStorage.removeItem('supabase.auth.token')
        sessionStorage.removeItem('supabase.auth.token')
        // 清除所有可能的 Supabase 相关存储
        Object.keys(localStorage).forEach(key => {
          if (key.includes('supabase') || key.includes('sb-')) {
            localStorage.removeItem(key)
          }
        })
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('supabase') || key.includes('sb-')) {
            sessionStorage.removeItem(key)
          }
        })
      } catch (storageError) {
        // 忽略存储清理错误
      }
      
    } catch (error) {
      // 即使出现异常，也清除本地状态
      setState({
        user: null,
        loading: false,
        error: null,
        isLoggingOut: false
      })
    }
  }

  // 清除错误
  const clearError = () => {
    setState(prev => ({ ...prev, error: null }))
  }

  const value: AuthContextType = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    isLoggingOut: state.isLoggingOut,
    login,
    register,
    logout,
    clearError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 